import React, { useCallback, useEffect, useState } from "react";
import { Radar, Bell, BellOff, Loader2, Ticket, Check } from "lucide-react";
import api from "../../api";
import { useLang } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { startCheckout } from "../../utils/checkout";
import { catLabel } from "../../utils/categoryLabel";
import { useEnabledCategoryNames } from "../../hooks/useEnabledCategories";

const money = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "$0";
  return `$${v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)}`;
};
const whenLabel = (iso) => {
  const d = new Date(iso || 0);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

const Pips = ({ used, limit }) => (
  <span className="inline-flex items-center gap-1" aria-hidden="true">
    {Array.from({ length: limit }).map((_, i) => (
      <span key={i} className={`h-2 w-2 rounded-full ${i < used ? "bg-[#f5b942]" : "bg-[#16a34a]"}`} />
    ))}
  </span>
);

// The paid-slot pool is invisible unless we show it: three sponsored spots per category within
// 10km, who holds them, and a watch that pings the shop the moment one frees.
export const SlotRadar = () => {
  const allCats = useEnabledCategoryNames();
  const { t } = useLang();
  const { user } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [pick, setPick] = useState("");

  const load = useCallback(() => {
    api.get("/boost/radar").then((r) => setData(r.data)).catch(() => setData({ categories: [] }));
  }, []);
  useEffect(load, [load]);

  const toggleWatch = async (row) => {
    setBusy(row.category);
    try {
      const { data: res } = await api.post("/boost/watch", { category: row.category, on: !row.watching });
      setData((d) => ({ ...d, categories: d.categories.map((c) => (c.category === row.category ? { ...c, watching: res.watching } : c)) }));
      toast({ title: res.watching ? t("sr.watchOn", { category: row.category }) : t("sr.watchOff", { category: row.category }) });
    } catch (e) { toast({ title: e.response?.data?.detail || t("acc.pleaseTryAgain") }); }
    finally { setBusy(""); }
  };

  const watchNew = async () => {
    if (!pick) return;
    setBusy(pick);
    try {
      await api.post("/boost/watch", { category: pick, on: true });
      toast({ title: t("sr.watchOn", { category: pick }) });
      setPick("");
      load();
    } catch (e) { toast({ title: e.response?.data?.detail || t("acc.pleaseTryAgain") }); }
    finally { setBusy(""); }
  };

  const buyPass = async () => {
    setBusy("pass");
    try { await startCheckout("boost_pass"); }
    catch (e) { toast({ title: t("prc.checkoutFail"), description: e.response?.data?.detail || t("prc.tryAgain") }); setBusy(""); }
  };

  if (!data) {
    return <div className="bg-card border border-border rounded-2xl p-6 flex items-center gap-2 text-sm text-muted-foreground" data-testid="slot-radar-loading"><Loader2 className="animate-spin" size={15} /> {t("sr.loading")}</div>;
  }
  if (!data.categories) return null;

  return (
    <div className="bg-card border border-border rounded-2xl p-6" data-testid="slot-radar">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-extrabold text-foreground flex items-center gap-2"><Radar size={18} className="text-[#16a34a]" /> {t("sr.title")}</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{t("sr.sub", { limit: data.categories[0]?.limit ?? 3, km: data.radiusKm ?? 10 })}</p>
        </div>
        {data.boostPasses > 0 ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f] px-3 py-1.5 rounded-xl" data-testid="pass-balance">
            <Ticket size={14} /> {t("sr.passesHeld", { n: data.boostPasses })}
          </span>
        ) : !user?.verified ? (
          <span className="text-xs font-semibold text-muted-foreground" data-testid="pass-needs-verification">
            {t("sr.passNeedsVerification")}
          </span>
        ) : (
          <button onClick={buyPass} disabled={busy === "pass"} data-testid="buy-boost-pass-btn"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 px-3 py-2 rounded-xl transition-colors">
            {busy === "pass" ? <Loader2 className="animate-spin" size={14} /> : <Ticket size={14} />} {t("sr.buyPass", { price: money(data.passPrice) })}
          </button>
        )}
      </div>

      <div className="mt-4 divide-y divide-border">
        {data.categories.map((c) => {
          const full = c.remaining <= 0;
          return (
            <div key={c.category} className="flex flex-wrap items-center gap-3 py-3" data-testid={`radar-row-${c.category}`}>
              <div className="flex-1 min-w-[180px]">
                <p className="font-bold text-foreground text-sm flex items-center gap-2">
                  {catLabel(c.category, t)} <Pips used={c.used} limit={c.limit} />
                </p>
                <p className={`text-xs mt-0.5 ${full ? "text-[#b8860b]" : "text-muted-foreground"}`} data-testid={`radar-state-${c.category}`}>
                  {c.exclusiveUntil
                    ? t("sr.exclusiveHeld", { date: whenLabel(c.exclusiveUntil) })
                    : full
                      ? (c.nextFreeAt ? t("sr.fullNext", { limit: c.limit, date: whenLabel(c.nextFreeAt) }) : t("sr.full", { limit: c.limit }))
                      : t("sr.open", { n: c.remaining, limit: c.limit, km: c.radiusKm ?? 10 })}
                </p>
              </div>
              <button onClick={() => toggleWatch(c)} disabled={busy === c.category} data-testid={`radar-watch-${c.category}`}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl border transition-colors shrink-0 ${c.watching ? "border-[#16a34a] text-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f]" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                {busy === c.category ? <Loader2 className="animate-spin" size={13} /> : c.watching ? <Check size={13} /> : full ? <Bell size={13} /> : <BellOff size={13} />}
                {c.watching ? t("sr.watching") : t("sr.watch")}
              </button>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground mt-3" data-testid="slot-radar-note">{t("sr.note")}</p>

      {/* A shop can wait on a category it hasn't posted in yet — that's how it breaks into one. */}
      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3" data-testid="radar-add-category">
        <label className="text-xs font-semibold text-muted-foreground">{t("sr.watchAnother")}</label>
        <select value={pick} onChange={(e) => setPick(e.target.value)} data-testid="radar-category-select"
          className="h-9 rounded-xl border border-border bg-card px-2 text-sm font-semibold text-foreground outline-none focus:border-[#16a34a]">
          <option value="">{t("sr.pickCategory")}</option>
          {allCats.filter((n) => !data.categories.some((c) => c.category === n)).map((n) => (
            <option key={n} value={n}>{catLabel(n, t)}</option>
          ))}
        </select>
        <button onClick={watchNew} disabled={!pick || busy === pick} data-testid="radar-add-watch-btn"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#0f2a1a] hover:bg-[#16341f] disabled:opacity-60 px-3 py-2 rounded-xl transition-colors">
          {busy === pick && pick ? <Loader2 className="animate-spin" size={13} /> : <Bell size={13} />} {t("sr.watch")}
        </button>
      </div>
    </div>
  );
};

export default SlotRadar;
